
import { Form } from "@/components/ui/form";
import { PropBot } from "@/types/prop-bots";
import { useStartChallengeForm } from "@/hooks/use-start-challenge-form";
import { FormActions } from "@/components/premium-bot-detail/form-fields/FormActions";
import { PlatformField } from "@/components/premium-bot-detail/form-fields/PlatformField";
import { CTraderProfileField } from "@/components/premium-bot-detail/form-fields/CTraderProfileField";
import { TradingAccountField } from "@/components/premium-bot-detail/form-fields/TradingAccountField";
import { VolumeMultiplierField } from "@/components/premium-bot-detail/form-fields/VolumeMultiplierField";
import { ReversePositionField } from "@/components/common/ReversePositionField";
import { usePropTradingAccountValidation } from "@/hooks/use-prop-trading-account-validation";
import { useInstantAccountValidation } from "@/hooks/use-instant-account-validation";
import { InstantAccountInfoDialog } from "@/components/prop-trading/InstantAccountInfoDialog";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface StartChallengeFormProps {
  bot: PropBot;
  onSuccess: () => void;
  defaultReversePosition?: boolean;
}

export const StartChallengeForm = ({
  bot,
  onSuccess,
  defaultReversePosition,
}: StartChallengeFormProps) => {
  const queryClient = useQueryClient();
  const { canStartChallenge, getErrorMessage } = usePropTradingAccountValidation();
  const { 
    canConnectAccount, 
    getAccountValidationError, 
    isInstantAccountBot 
  } = useInstantAccountValidation(bot.id);
  
  const [showInstantAccountInfo, setShowInstantAccountInfo] = useState(false);
  const [proceedWithChallenge, setProceedWithChallenge] = useState(false);
  
  const {
    form,
    isLoadingTradingAccounts,
    tradingAccounts,
    isSubmitting,
    handleSubmit
  } = useStartChallengeForm(bot.id, async () => {
    // Invalidate relevant queries before calling onSuccess
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['propBotAccounts', bot.id] }),
      queryClient.invalidateQueries({ queryKey: ['propBotSignals', bot.id] }),
      queryClient.invalidateQueries({ queryKey: ['propBotSignalStatuses', bot.id] }),
      queryClient.invalidateQueries({ queryKey: ['resourceUsage'] })
    ]);
    onSuccess();
  });

  // Default reverse positions toggle based on context
  useEffect(() => {
    if (typeof defaultReversePosition === 'boolean') {
      form.setValue('is_reverse_position', defaultReversePosition);
    }
  }, [defaultReversePosition, form]);

  // Show instant account info dialog first for instant account bots
  useEffect(() => {
    if (isInstantAccountBot && !proceedWithChallenge) {
      setShowInstantAccountInfo(true);
      return;
    }
    
    if (!canStartChallenge()) {
      toast.error("Account Limit Reached", {
        description: getErrorMessage(),
      });
      onSuccess(); // Close the dialog
    }
  }, [canStartChallenge, getErrorMessage, onSuccess, isInstantAccountBot, proceedWithChallenge]);

  const handleInstantAccountContinue = () => {
    setShowInstantAccountInfo(false);
    setProceedWithChallenge(true);
  };

  const handleInstantAccountCancel = () => {
    setShowInstantAccountInfo(false);
    onSuccess(); // Close the main dialog
  };

  // Enhanced form submission with account validation
  const handleFormSubmit = (data: any) => {
    const selectedAccount = tradingAccounts?.find(acc => acc.id === data.trading_account_id);
    
    if (isInstantAccountBot && selectedAccount) {
      const accountError = getAccountValidationError(selectedAccount.live);
      if (accountError) {
        toast.error("Account Validation Error", {
          description: accountError,
        });
        return;
      }
    }
    
    handleSubmit(data);
  };

  return (
    <>
      <InstantAccountInfoDialog
        isOpen={showInstantAccountInfo}
        onClose={handleInstantAccountCancel}
        onContinue={handleInstantAccountContinue}
        botName={bot.name}
      />
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <PlatformField form={form} />
        <CTraderProfileField form={form} />
        <TradingAccountField
          form={form}
          isLoading={isLoadingTradingAccounts}
          accounts={tradingAccounts}
          hasCspProfile={!!form.watch('csp_account_id')}
        />
        <VolumeMultiplierField form={form} />
        <ReversePositionField form={form} />
        <FormActions
          isSubmitting={isSubmitting}
          onCancel={onSuccess}
        />
        </form>
      </Form>
    </>
  );
};
